import { useState } from 'react';
import { Modal, Skeleton, useAsync, useToast } from '../../components/ui';
import { LibraryItem, triggerText } from '../../journeys/model';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { errorText } from '../Contacts';

const text = (v: { ru: string; en: string } | string, locale: 'ru' | 'en') => (typeof v === 'string' ? v : v[locale]);

/**
 * Gallery of library journeys (docs/journeys/SPEC.md §3.16, §4.1): what will be created (email templates by alias,
 * segments) and that nothing starts before an approval.
 */
export function JourneyLibrary({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const toast = useToast();
  const lib = useAsync(() => api.get<{ items: LibraryItem[] }>('/journeys/library'));
  const [locale, setLocale] = useState<'ru' | 'en'>('ru');
  const [busy, setBusy] = useState<string | null>(null);

  async function use(item: LibraryItem) {
    setBusy(item.key);
    try {
      const r = await api.post<{ id: string; created: boolean; warnings?: { message: string }[] }>('/journeys', { library: item.key, locale });
      toast(r.created ? t('Черновик создан: проверьте шаги и запросите согласование') : t('Такой сценарий уже есть — открываю его'));
      onCreated(r.id);
    } catch (err) {
      toast(errorText(err), 'bad');
      setBusy(null);
    }
  }

  return (
    <Modal title={t('Сценарий из шаблона')} onClose={onClose}>
      <div className="row wrap">
        <span className="small muted">{t('Язык писем:')}</span>
        <div className="chips">
          <button className={`chip${locale === 'ru' ? ' on' : ''}`} onClick={() => setLocale('ru')}>{t('Русский')}</button>
          <button className={`chip${locale === 'en' ? ' on' : ''}`} onClick={() => setLocale('en')}>English</button>
        </div>
      </div>
      {lib.loading ? (
        <Skeleton h={160} />
      ) : (
        lib.data?.items.map((item) => (
          <div key={item.key} className="card card-pad stack" style={{ gap: 8 }}>
            <div className="row wrap" style={{ alignItems: 'flex-start' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h3 style={{ margin: 0 }}>{item.name}</h3>
                <div className="small muted">{triggerText(item.trigger)}</div>
              </div>
              <button className="btn btn-primary btn-sm" disabled={busy !== null} onClick={() => use(item)}>{busy === item.key ? t('Создаю…') : t('Использовать')}</button>
            </div>
            <p className="small" style={{ margin: 0 }}>{item.description}</p>
            <div className="small">
              <b>{t('Создастся:')}</b> {t('черновик сценария (версия 1) и шаблоны писем, которых ещё нет —')}{' '}
              {item.emails.map((e) => <span key={e.alias}><code>{e.alias}</code> «{text(e.subject, locale)}» </span>)}
              {item.segments.length > 0 && <> {t('и сегмент')} {item.segments.map((s) => <span key={s.key}><code>{s.key}</code> «{text(s.name, locale)}»</span>)}</>}. {t('Существующие шаблоны с тем же alias не перезаписываются.')}
            </div>
          </div>
        ))
      )}
      <div className="callout warn">{t('Ничего не запускается сразу: сценарий создаётся черновиком, запуск — после согласования владельцем или администратором.')}</div>
    </Modal>
  );
}
